// frontend/src/components/parent/ParentHeatmap.tsx

import { useState } from 'react';
import { HeatmapDay } from '../../types/parent.types';

interface ParentHeatmapProps {
  days: HeatmapDay[];
}

const MONTH_LABELS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const DAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

function getIntensityClass(intensity: number): string {
  if (intensity === 0) return 'hm-cell-0';
  if (intensity === 1) return 'hm-cell-1';
  if (intensity === 2) return 'hm-cell-2';
  return 'hm-cell-3';
}

function formatTooltip(day: HeatmapDay): string {
  const d = new Date(day.date);
  const dateStr = d.toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'long' });
  if (day.intensity === 0) return `${dateStr} — Aktif değil`;
  const xp = day.xpEarned ? ` | ${day.xpEarned} XP` : '';
  const acts = day.activityCount ? ` | ${day.activityCount} aktivite` : '';
  return `${dateStr}${xp}${acts}`;
}

export function ParentHeatmap({ days }: ParentHeatmapProps) {
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  // Haftalık gruplar için gün-haftası hesapla
  const weeks: HeatmapDay[][] = [];
  let currentWeek: HeatmapDay[] = [];

  for (const day of days) {
    const d = new Date(day.date);
    const dow = (d.getDay() + 6) % 7; // 0=Pzt, 6=Paz
    if (dow === 0 && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    currentWeek.push(day);
  }
  if (currentWeek.length > 0) weeks.push(currentWeek);

  // Ay etiketleri
  const monthLabels: { label: string; weekIdx: number }[] = [];
  weeks.forEach((week, wi) => {
    const d = new Date(week[0].date);
    if (wi === 0 || d.getDate() <= 7) {
      monthLabels.push({ label: MONTH_LABELS[d.getMonth()], weekIdx: wi });
    }
  });

  const activeDays = days.filter((d) => d.intensity > 0).length;

  return (
    <div className="pcd-section-card">
      <div className="pcd-section-header">
        <div className="pcd-section-indicator" style={{ background: '#00AEEF' }} />
        <h3 className="pcd-section-title">Aktivite Haritası</h3>
        <span className="pcd-heatmap-summary">{activeDays} aktif gün</span>
      </div>

      <div className="pcd-heatmap-wrap">
        {/* Gün etiketleri */}
        <div className="pcd-heatmap-day-labels">
          {DAY_LABELS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className="pcd-heatmap-grid-wrap">
          {/* Ay etiketleri */}
          <div className="pcd-heatmap-month-row">
            {weeks.map((_, wi) => {
              const ml = monthLabels.find((m) => m.weekIdx === wi);
              return (
                <div key={wi} className="pcd-heatmap-month-cell">
                  {ml ? ml.label : ''}
                </div>
              );
            })}
          </div>

          {/* Hücre grid */}
          <div className="pcd-heatmap-grid">
            {weeks.map((week, wi) => (
              <div key={wi} className="pcd-heatmap-week">
                {week.map((day) => (
                  <div
                    key={day.date}
                    className={`pcd-heatmap-cell ${getIntensityClass(day.intensity)}`}
                    onMouseEnter={(e) => {
                      const rect = (e.target as HTMLElement).getBoundingClientRect();
                      setTooltip({ text: formatTooltip(day), x: rect.left, y: rect.top - 36 });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="pcd-heatmap-legend">
        <span>Az</span>
        <div className="pcd-heatmap-cell hm-cell-0" />
        <div className="pcd-heatmap-cell hm-cell-1" />
        <div className="pcd-heatmap-cell hm-cell-2" />
        <div className="pcd-heatmap-cell hm-cell-3" />
        <span>Çok</span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="pcd-heatmap-tooltip"
          style={{ position: 'fixed', left: tooltip.x, top: tooltip.y, pointerEvents: 'none' }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
